<template>
  <div
    class="relative base-input"
    :class="{ error: hasErrors, disabled }"
  >
    <div class="flex between-xs">
      <label
        class="inline-block"
        :for="inputId"
      >
        <span class="inline-block">
          {{ label }}
        </span>
        <span
          v-if="required"
          class="c-primary inline-block"
        >*</span>
      </label>
      <validation-messages
        v-if="validations"
        :show-text="!hasFocus"
        :validations="validations"
      />
    </div>
    <div class="relative">
      <span
        v-if="showCurrency"
        class="absolute currency-sign"
      >{{ currencySign }}</span>
      <input
        :id="inputId"
        :ref="name"
        class="form-input"
        :class="{empty: value === '', pl30: showCurrency}"
        type="number"
        :aria-label="ariaLabel"
        :min="min"
        :max="max"
        :name="name"
        :value="value"
        :autofocus="autofocus"
        :placeholder="placeholder"
        :disabled="disabled"
        @input="$emit('input', $event.target.value)"
        @focus="onFocus"
        @blur="onBlur"
        @keyup.enter="$emit('keyup.enter', $event.target.value)"
        @keyup="$emit('keyup', $event)"
      >
    </div>
  </div>
</template>

<script>
import { currentStoreView } from '@vue-storefront/core/lib/multistore'
import ValidationMessages from './ValidationMessages.vue'

export default {
  name: 'BaseInputNumber',
  components: {
    ValidationMessages
  },
  props: {
    value: {
      type: [String, Number],
      default: 0
    },
    showCurrency: {
      type: Boolean,
      default: false
    },
    name: {
      type: String,
      required: false,
      default: ''
    },
    ariaLabel: {
      type: String,
      required: false,
      default: ''
    },
    id: {
      type: String,
      default: null
    },
    required: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      required: false,
      default: ''
    },
    placeholder: {
      type: String,
      required: false,
      default: ''
    },
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: undefined
    },
    disabled: {
      type: Boolean,
      default: false
    },
    autofocus: {
      type: Boolean,
      required: false,
      default: false
    },
    validations: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      hasFocus: false
    }
  },
  computed: {
    inputId () {
      return this.id || `input_${this._uid}`
    },
    hasErrors () {
      if (this.validations && this.validations.length > 0) {
        return this.validations.some(validation => !!validation.condition)
      }

      return false
    },
    currencySign () {
      return currentStoreView().i18n.currencySign
    }
  },
  mounted () {
    if (this.focus) {
      this.$refs[this.name].focus()
    }
  },
  methods: {
    // setFocus sets focus on a field which has a value of 'ref' tag equal to fieldName
    setFocus (fieldName = this.name) {
      if (this.name === fieldName) {
        this.$refs[this.name].focus()
      }
    },
    onFocus () {
      this.$emit('focus')
      this.hasFocus = true
    },
    onBlur () {
      this.$emit('blur')
      this.hasFocus = false
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~theme/css/variables/colors';
@import '~theme/css/helpers/functions/color';
@import '~theme/css/variables/grid';

.currency-sign {
  top: 50%;
  transform: translateY(-50%);
  left: 20px;
  line-height: 1;
  margin-top: 1px;
}

.base-input-number {
  &__input {
    border-style: solid;
    border-width: 0 0 1px 0;
    width: 50px;
    height: 1.4rem;
    line-height: 1.7rem;

    @include desktop {
      height: 1.7rem;
    }
  }

  &__label {
    font-size: 0.8rem;
    line-height: 1.2rem;

    @include desktop {
      font-size: 1rem;
      line-height: 1.4rem;
    }
  }
}
</style>
